/*
  /apply — Shared form + success + design tokens.

  ⚠️  DO NOT change the submission contract, field IDs, validation
  rules, honeypot, error handling or success-screen copy without
  reviewing `test_apply_beta_applications.py` — the 24 security tests
  depend on this exact behavior.

  All components exported here are consumed by the parent
  `ApplyPage.jsx`.  They are intentionally scoped to `/apply` and
  never rendered anywhere else in the SPA.
*/

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

// ── Design tokens ─────────────────────────────────────────────────
export const COLORS = {
  bgDark:    "#0F0E0C",           // near-black charcoal, warm tint
  bgDarkAlt: "#171512",
  bgDeep:    "#0A0908",
  ivory:     "#F4EFE4",           // warm ivory card
  ivorySoft: "#EEE7D8",
  paper:     "#FBFAF3",
  ink:       "#1C1C1C",
  inkSoft:   "#5C5C5C",
  gold:      "#C8A97E",           // brushed gold
  goldSoft:  "#A68A64",
  goldDeep:  "#8B6F44",
  divider:   "rgba(200,169,126,0.28)",
  cardEdge:  "#E2DACB",
  border:    "rgba(244,239,228,0.10)",
};

export const SERIF = { fontFamily: "'Cormorant Garamond', 'Times New Roman', serif" };
export const SANS  = { fontFamily: "'Manrope', system-ui, sans-serif" };

// ── Backend contract (must mirror server allow-lists) ─────────────
const API = process.env.REACT_APP_BACKEND_URL;

export const BUSINESS_TYPES = [
  { v: "media_wall_specialist", label: "Media-wall specialist" },
  { v: "joinery_cabinetry",     label: "Joinery / cabinetry" },
  { v: "interior_designer",     label: "Interior designer" },
  { v: "architect",             label: "Architect" },
  { v: "av_integrator",         label: "AV integrator" },
  { v: "builder",               label: "Builder" },
  { v: "other",                 label: "Other" },
];
export const VOLUME_BANDS = [
  { v: "1_5",      label: "1 – 5" },
  { v: "6_15",     label: "6 – 15" },
  { v: "16_40",    label: "16 – 40" },
  { v: "41_100",   label: "41 – 100" },
  { v: "100_plus", label: "100+" },
];
export const TOOLS = [
  { v: "sketchup",           label: "SketchUp" },
  { v: "autocad",            label: "AutoCAD" },
  { v: "revit",              label: "Revit" },
  { v: "fusion_360",         label: "Fusion 360" },
  { v: "microvellum",        label: "Microvellum" },
  { v: "cabinet_vision",     label: "Cabinet Vision" },
  { v: "quickbooks",         label: "QuickBooks" },
  { v: "xero",               label: "Xero" },
  { v: "excel_spreadsheets", label: "Excel / Sheets" },
  { v: "paper_manual",       label: "Paper / manual" },
  { v: "other",              label: "Other" },
];

const parseUtm = () => {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out = {};
  ["utm_source","utm_medium","utm_campaign","utm_term","utm_content"].forEach(k => {
    const v = p.get(k); if (v) out[k] = v;
  });
  if (document?.referrer) out.referrer = document.referrer.slice(0, 300);
  return out;
};

// ── Lightweight first-party event dispatch (window CustomEvent only;
//    no third-party tracking, no PII).  If the host page listens on
//    `luxe:apply-event` it can wire analytics.
export const trackEvent = (name, payload = {}) => {
  try {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("luxe:apply-event", {
      detail: { name, payload, ts: Date.now() },
    }));
  } catch (_) { /* never break the UI on analytics */ }
};

// ── Success screen ────────────────────────────────────────────────
export function SuccessPanel({ country }) {
  const isAU = country === "AU";
  const heading = isAU ? "Application received" : "You’re on the UK early-access list";
  const body = isAU
    ? "Thanks — we’ve received your application. We’re keeping the first Subtend Studio beta intentionally small so we can work closely with every foundation studio. We’ll review your application and aim to be in touch within 3 business days."
    : "Thanks for your interest in Subtend Studio. We’ll review your details and keep you updated as UK beta places become available.";
  const label = isAU ? "Paid Private Beta" : "UK Early Access";
  return (
    <div data-testid={`apply-success-${country?.toLowerCase()}`}
         className="page-anim"
         style={{ background: COLORS.ivory, padding: "48px 40px",
                  border: `1px solid ${COLORS.cardEdge}` }}>
      <div style={{ ...SANS, letterSpacing: "0.24em", textTransform: "uppercase",
                    color: COLORS.goldDeep, fontSize: 11, fontWeight: 600, marginBottom: 20 }}>
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <span aria-hidden="true"
              style={{ width: 44, height: 44, borderRadius: "50%",
                       background: COLORS.gold, display: "inline-flex",
                       alignItems: "center", justifyContent: "center",
                       color: COLORS.bgDark, fontSize: 22, fontWeight: 600, ...SERIF }}>✓</span>
        <h1 style={{ ...SERIF, fontSize: 34, lineHeight: 1.1,
                     color: COLORS.ink, margin: 0, fontWeight: 500 }}>
          {heading}
        </h1>
      </div>
      <p style={{ ...SANS, color: COLORS.inkSoft, fontSize: 15, lineHeight: 1.7,
                  margin: 0, maxWidth: 540 }}>
        {body}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 32 }}>
        <Link to="/" data-testid="apply-success-cta-features"
              style={{ ...SANS, background: COLORS.ink, color: COLORS.ivory,
                       padding: "14px 26px", fontSize: 14, letterSpacing: "0.04em",
                       textDecoration: "none", fontWeight: 500 }}>
          Explore Subtend Studio
        </Link>
        <Link to="/" data-testid="apply-success-cta-home"
              style={{ ...SANS, background: "transparent", color: COLORS.ink,
                       padding: "14px 26px", fontSize: 14, letterSpacing: "0.04em",
                       textDecoration: "none", fontWeight: 500,
                       border: `1px solid ${COLORS.cardEdge}` }}>
          Return to homepage
        </Link>
      </div>
    </div>
  );
}

// ── Country card ──────────────────────────────────────────────────
export function CountryCard({ code, flag, title, subtitle, tone, active, onSelect }) {
  return (
    <button
      type="button"
      data-testid={`apply-country-${code.toLowerCase()}`}
      onClick={() => onSelect(code)}
      style={{
        ...SANS, cursor: "pointer", textAlign: "left",
        background: active ? COLORS.ivory : "rgba(255,255,255,0.55)",
        border: `1px solid ${active ? COLORS.gold : COLORS.cardEdge}`,
        padding: "24px 26px", color: COLORS.ink,
        transition: "background .2s, border-color .2s, color .2s",
        width: "100%", display: "flex", gap: 18, alignItems: "flex-start",
      }}
    >
      <div style={{ fontSize: 32, lineHeight: 1 }} aria-hidden="true">{flag}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...SERIF, fontSize: 22, fontWeight: 500, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, letterSpacing: "0.06em",
                      color: COLORS.goldDeep,
                      textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
          {tone}
        </div>
        <div style={{ fontSize: 13.5, lineHeight: 1.6,
                      color: COLORS.inkSoft }}>
          {subtitle}
        </div>
      </div>
    </button>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────
function Field({ label, required, error, children, testid, hint }) {
  return (
    <label data-testid={testid ? `${testid}-field` : undefined}
           style={{ display: "block", marginBottom: 20 }}>
      <div style={{ ...SANS, fontSize: 12, letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: COLORS.goldDeep, fontWeight: 600, marginBottom: 8 }}>
        {label}{required && <span style={{ color: "#B0654C", marginLeft: 4 }}>*</span>}
      </div>
      {children}
      {hint && !error && (
        <div style={{ ...SANS, fontSize: 12, color: COLORS.inkSoft, marginTop: 6 }}>{hint}</div>
      )}
      {error && (
        <div data-testid={testid ? `${testid}-error` : undefined}
             style={{ ...SANS, fontSize: 12.5, color: "#8E3B26", marginTop: 6 }}>{error}</div>
      )}
    </label>
  );
}

const inputStyle = {
  ...SANS, width: "100%", boxSizing: "border-box",
  background: COLORS.paper, border: `1px solid ${COLORS.cardEdge}`,
  padding: "12px 14px", fontSize: 14.5, color: COLORS.ink,
  outline: "none", borderRadius: 2,
};

// ── Country selector block ────────────────────────────────────────
export function CountrySelector({ selected, onSelect }) {
  return (
    <div data-testid="apply-country-selector"
         className="page-anim"
         style={{ marginBottom: 28 }}>
      <div style={{ ...SANS, letterSpacing: "0.24em", textTransform: "uppercase",
                    color: COLORS.goldDeep, fontSize: 11, fontWeight: 600, marginBottom: 14 }}>
        Step 1 · Choose your studio market
      </div>
      <div className="apply-country-grid"
           style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
        <CountryCard
          code="AU" flag="🇦🇺" title="Australia"
          tone="Paid Private Beta"
          subtitle="Foundation-studio pricing, direct product access, and weekly build calls. Intentionally limited to a small group of studios."
          active={selected === "AU"} onSelect={onSelect}/>
        <CountryCard
          code="UK" flag="🇬🇧" title="United Kingdom"
          tone="Early Access List"
          subtitle="Join the UK early-access list and we’ll be in touch as beta places open. GBP + VAT localisation still in preparation."
          active={selected === "UK"} onSelect={onSelect}/>
      </div>
    </div>
  );
}

// ── Application form (identical submission contract) ──────────────
export function ApplicationForm({ country, onSubmitted, onBack }) {
  const isAU = country === "AU";
  const programLabel = isAU ? "Paid Private Beta · Australia" : "Early Access · United Kingdom";
  const utm = useMemo(() => parseUtm(), []);

  const [form, setForm] = useState({
    business_name: "", contact_name: "", email: "", phone: "", website: "",
    region: "", business_type: "", annual_project_volume: "",
    current_tools: [], biggest_frustration: "",
    referral_source: "", marketing_consent: false,
    honeypot: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [startedTracked, setStartedTracked] = useState(false);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(err => ({ ...err, [k]: undefined }));
    if (!startedTracked) {
      setStartedTracked(true);
      trackEvent("application_started", { country });
    }
  };
  const toggleTool = (v) => setForm(f => ({
    ...f,
    current_tools: f.current_tools.includes(v)
      ? f.current_tools.filter(x => x !== v)
      : [...f.current_tools, v],
  }));

  const validate = () => {
    const e = {};
    if (form.business_name.trim().length < 2) e.business_name = "Studio name is required.";
    if (form.contact_name.trim().length  < 2) e.contact_name  = "Contact name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email.";
    if (isAU && form.phone.trim().length < 6) e.phone = "Phone is required for AU applications.";
    if (form.region.trim().length < 2)  e.region = "Region is required.";
    if (!form.business_type)            e.business_type = "Choose your business type.";
    if (!form.annual_project_volume)    e.annual_project_volume = "Choose an annual project volume.";
    if (form.biggest_frustration.trim().length < 10)
      e.biggest_frustration = "A short description (10+ characters) helps us qualify your studio.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setGlobalError("");
    if (!validate()) return;
    setSubmitting(true);
    trackEvent("application_submitted", { country });
    try {
      const payload = { country, utm, ...form };
      const res = await fetch(`${API}/api/public/beta-applications`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (res.status === 429) {
        setGlobalError("Too many submissions from this network. Please try again later.");
      } else if (res.status === 400) {
        const data = await res.json().catch(() => ({}));
        const detail = data.detail;
        if (detail && typeof detail === "object" && detail.field) {
          setErrors({ [detail.field]: detail.message || "Please check this field." });
        } else {
          setGlobalError("Please review the form and try again.");
        }
      } else if (res.ok) {
        trackEvent("application_success", { country });
        onSubmitted();
      } else {
        setGlobalError("Something went wrong. Please try again in a moment.");
      }
    } catch (err) {
      setGlobalError("We couldn’t reach the server. Please try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid={`apply-form-${country.toLowerCase()}`}
          className="page-anim"
          style={{ background: COLORS.ivory, padding: "36px 34px 40px",
                   border: `1px solid ${COLORS.cardEdge}` }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                    justifyContent: "space-between", gap: 12, marginBottom: 22 }}>
        <div>
          <div style={{ ...SANS, fontSize: 11, letterSpacing: "0.24em",
                        textTransform: "uppercase", color: COLORS.goldDeep,
                        fontWeight: 600, marginBottom: 8 }}>
            {programLabel}
          </div>
          <h2 style={{ ...SERIF, fontSize: 30, color: COLORS.ink, margin: 0,
                       fontWeight: 500, lineHeight: 1.1 }}>
            Tell us about your studio
          </h2>
        </div>
        <button type="button" onClick={onBack} data-testid="apply-form-back"
                style={{ ...SANS, background: "transparent",
                         border: `1px solid ${COLORS.cardEdge}`,
                         padding: "8px 14px", cursor: "pointer", fontSize: 12.5,
                         color: COLORS.inkSoft, letterSpacing: "0.06em" }}>
          ← Change country
        </button>
      </div>

      <Field label="Studio / Business name" required error={errors.business_name} testid="apply-business-name">
        <input data-testid="apply-business-name" style={inputStyle} maxLength={140}
               value={form.business_name} onChange={set("business_name")} autoComplete="organization"/>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 0 }} className="apply-grid-two">
        <Field label="Contact name" required error={errors.contact_name} testid="apply-contact-name">
          <input data-testid="apply-contact-name" style={inputStyle} maxLength={140}
                 value={form.contact_name} onChange={set("contact_name")} autoComplete="name"/>
        </Field>
        <Field label="Work email" required error={errors.email} testid="apply-email">
          <input data-testid="apply-email" type="email" style={inputStyle} maxLength={254}
                 value={form.email} onChange={set("email")} autoComplete="email"/>
        </Field>
      </div>

      <div className="apply-grid-two" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 0 }}>
        <Field label={`Phone${isAU ? "" : " (optional)"}`}
               required={isAU} error={errors.phone} testid="apply-phone">
          <input data-testid="apply-phone" style={inputStyle} maxLength={40}
                 value={form.phone} onChange={set("phone")} autoComplete="tel"/>
        </Field>
        <Field label="Website or social (optional)" testid="apply-website"
               hint="Instagram or Facebook is fine if you don’t have a website.">
          <input data-testid="apply-website" style={inputStyle} maxLength={300}
                 value={form.website} onChange={set("website")} placeholder="yourstudio.com"/>
        </Field>
      </div>

      <Field label={isAU ? "Region / State" : "Region / City"} required
             error={errors.region} testid="apply-region"
             hint={isAU ? "e.g. Sunshine Coast QLD, or Sydney NSW"
                        : "e.g. London, Manchester, Edinburgh"}>
        <input data-testid="apply-region" style={inputStyle} maxLength={140}
               value={form.region} onChange={set("region")} autoComplete="address-level1"/>
      </Field>

      <Field label="Business type" required error={errors.business_type} testid="apply-business-type">
        <select data-testid="apply-business-type" style={inputStyle}
                value={form.business_type} onChange={set("business_type")}>
          <option value="">Select…</option>
          {BUSINESS_TYPES.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      </Field>

      <Field label="Annual media-wall / built-in project volume" required
             error={errors.annual_project_volume} testid="apply-annual-project-volume"
             hint="Roughly how many built-in or media-wall projects you deliver per year.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(88px, 1fr))", gap: 8 }}>
          {VOLUME_BANDS.map(b => {
            const active = form.annual_project_volume === b.v;
            return (
              <button key={b.v} type="button"
                      data-testid={`apply-volume-${b.v}`}
                      onClick={() => set("annual_project_volume")(b.v)}
                      style={{ ...SANS, cursor: "pointer",
                               background: active ? COLORS.ink : "transparent",
                               color: active ? COLORS.ivory : COLORS.ink,
                               border: `1px solid ${active ? COLORS.ink : COLORS.cardEdge}`,
                               padding: "10px 8px", fontSize: 14, fontWeight: 500 }}>
                {b.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Current tools (select any that apply)" testid="apply-current-tools">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TOOLS.map(t => {
            const active = form.current_tools.includes(t.v);
            return (
              <button key={t.v} type="button"
                      data-testid={`apply-tool-${t.v}`}
                      onClick={() => toggleTool(t.v)}
                      style={{ ...SANS, cursor: "pointer",
                               background: active ? COLORS.gold : "transparent",
                               color: active ? COLORS.bgDark : COLORS.ink,
                               border: `1px solid ${active ? COLORS.gold : COLORS.cardEdge}`,
                               padding: "8px 14px", fontSize: 13, fontWeight: 500,
                               borderRadius: 999 }}>
                {t.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Biggest workflow frustration today" required
             error={errors.biggest_frustration} testid="apply-biggest-frustration"
             hint="A sentence or two is enough. Where do quotes, proposals or design revisions cost you the most time?">
        <textarea data-testid="apply-biggest-frustration"
                  style={{ ...inputStyle, minHeight: 108, resize: "vertical" }}
                  maxLength={1200}
                  value={form.biggest_frustration} onChange={set("biggest_frustration")}/>
      </Field>

      <Field label="How did you hear about Subtend Studio? (optional)" testid="apply-referral-source">
        <input data-testid="apply-referral-source" style={inputStyle} maxLength={300}
               value={form.referral_source} onChange={set("referral_source")}
               placeholder="Instagram, an architect, a supplier…"/>
      </Field>

      <label style={{ ...SANS, display: "flex", alignItems: "flex-start", gap: 10,
                      fontSize: 13.5, color: COLORS.inkSoft, lineHeight: 1.55,
                      margin: "4px 0 24px", cursor: "pointer" }}>
        <input type="checkbox" data-testid="apply-marketing-consent"
               checked={form.marketing_consent} onChange={set("marketing_consent")}
               style={{ marginTop: 3, accentColor: COLORS.goldDeep }}/>
        <span>
          I’d like occasional updates from Subtend Studio about the beta and product roadmap. You can unsubscribe at any time.
        </span>
      </label>

      {/* Honeypot — must remain empty. Hidden from users + AT. */}
      <div aria-hidden="true"
           style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}>
        <label>Company website (leave blank)
          <input type="text" tabIndex={-1} autoComplete="off"
                 value={form.honeypot} onChange={set("honeypot")}/>
        </label>
      </div>

      {globalError && (
        <div data-testid="apply-form-error"
             style={{ ...SANS, background: "#FBEEE8", color: "#8E3B26",
                      padding: "12px 14px", fontSize: 13.5,
                      border: "1px solid #E8CBC0", marginBottom: 18 }}>
          {globalError}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <button type="submit" disabled={submitting} data-testid="apply-submit"
                style={{ ...SANS, background: COLORS.ink, color: COLORS.ivory,
                         border: "none", padding: "14px 30px", fontSize: 14,
                         letterSpacing: "0.06em", fontWeight: 500,
                         cursor: submitting ? "not-allowed" : "pointer",
                         opacity: submitting ? 0.6 : 1 }}>
          {submitting ? "Sending…" : (isAU ? "Apply for Paid Beta" : "Join UK early access")}
        </button>
        <div style={{ ...SANS, fontSize: 12.5, color: COLORS.inkSoft }}>
          No account is created. We review every application manually.
        </div>
      </div>
    </form>
  );
}
